import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import LiveStopsMap from "@/components/LiveStopsMap";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [live, setLive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [manualPw, setManualPw] = useState(false);
  const loadLive = useCallback(() => api.get("/business/live").then((r) => setLive(r.data)).catch(() => {}), []);
  const load = useCallback(() => {
    api.get("/business/drivers").then((r) => setDrivers(r.data));
    api.get("/business/jobs").then((r) => setJobs(r.data));
    loadLive();
  }, [loadLive]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(loadLive, 15000); return () => clearInterval(t); }, [loadLive]);

  const add = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    if (manualPw && form.password.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      const payload = { name: form.name, email: form.email, phone: form.phone, origin_url: window.location.origin };
      if (manualPw && form.password) payload.password = form.password;
      const { data } = await api.post("/business/drivers", payload);
      toast.success(data.set_password_email_sent ? "Driver added — a login link was emailed" : "Driver added with the password you set");
      setOpen(false); setForm({ name: "", email: "", phone: "", password: "" }); setManualPw(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeDriver = async (id) => { if (!window.confirm("Remove this driver?")) return; await api.delete(`/business/drivers/${id}`); load(); };
  const assign = async (jobId, driverId) => { await api.put(`/business/jobs/${jobId}/assign`, { driver_id: driverId || null }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Drivers & jobs</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-driver-btn"><Plus className="mr-1 h-4 w-4" /> Add driver</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add driver</DialogTitle><DialogDescription>Add an internal driver for pickups and deliveries.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="driver-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="driver-email" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="driver-phone" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={manualPw} onChange={(e) => setManualPw(e.target.checked)} data-testid="driver-manual-pw-toggle" className="h-4 w-4 rounded border-slate-300 text-brand" />
                Set a password manually (skip the email link)
              </label>
              {manualPw ? (
                <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="At least 6 characters" data-testid="driver-password" /><p className="text-xs text-slate-400">Share this password with the driver directly.</p></div>
              ) : (
                <p className="text-xs text-slate-400">A set-password email will be sent so the driver can log in.</p>
              )}
            </div>
            <DialogFooter><Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="driver-save-btn">Add driver</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {live && live.drivers.length > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="live-status-panel">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" /></span>
            <h2 className="font-heading font-bold text-slate-900">Today&rsquo;s live activity</h2>
            <span className="text-xs text-slate-400">auto-refreshes · {live.date}</span>
          </div>
          <LiveStopsMap hq={live.hq} drivers={live.drivers} />
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {live.drivers.map((d) => (
              <div key={d.driver_id} className="rounded-xl border border-slate-100 p-4" data-testid={`live-driver-${d.driver_id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-800">{d.name}</p>
                  <div className="flex gap-1.5 text-xs">
                    <Badge className="border-0 bg-green-100 text-green-700">{d.done} done</Badge>
                    <Badge className="border-0 bg-amber-100 text-amber-700">{d.arrived} arrived</Badge>
                    <Badge className="border-0 bg-slate-100 text-slate-600">{d.pending} to go</Badge>
                  </div>
                </div>
                <ol className="mt-3 max-h-56 space-y-1.5 overflow-auto pr-1">
                  {d.jobs.map((j) => {
                    const state = j.status === "completed" ? "done" : (j.arrived_at ? "arrived" : "pending");
                    const dot = { done: "bg-green-500", arrived: "bg-amber-500", pending: "bg-slate-300" }[state];
                    const label = j.order_ref === "PICKUP" ? "Pickup request" : `#${j.order_ref}`;
                    return (
                      <li key={j.id} className="flex items-center gap-2 text-sm" data-testid={`live-job-${j.id}`}>
                        <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
                        <span className="font-medium text-slate-700">{label}</span>
                        <span className="capitalize text-slate-400">{j.type}</span>
                        <span className="truncate text-slate-500">· {j.client_name}</span>
                        <span className="ml-auto text-xs text-slate-400">
                          {j.completed_at ? `done ${j.completed_at.slice(11, 16)}` : (j.arrived_at ? `arrived ${j.arrived_at.slice(11, 16)}` : "en route")}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
          {live.unassigned.length > 0 && <p className="mt-3 text-xs text-slate-400" data-testid="live-unassigned">{live.unassigned.length} job(s) scheduled today are still unassigned.</p>}
        </div>
      )}

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-400">Drivers</h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {drivers.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4" data-testid={`driver-card-${d.id}`}>
            <div>
              <p className="font-medium text-slate-900">{d.name}</p>
              <p className="text-xs text-slate-400">{d.email}</p>
              <p className="mt-1 text-xs text-slate-500">{d.active_jobs} active · {d.completed_jobs} done</p>
            </div>
            <button onClick={() => removeDriver(d.id)} className="text-slate-300 hover:text-red-500" data-testid={`driver-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!drivers.length && <p className="text-sm text-slate-400">No drivers yet.</p>}
      </div>

      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-400">Jobs</h2>
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="jobs-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Driver</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map((j) => (
              <tr key={j.id} data-testid={`job-row-${j.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{j.order_ref}</td>
                <td className="px-4 py-3 capitalize">{j.type}</td>
                <td className="px-4 py-3">{j.client_name}</td>
                <td className="px-4 py-3 text-slate-500">{j.scheduled_date || "—"} {j.scheduled_slot}</td>
                <td className="px-4 py-3"><Badge className="border-0 bg-slate-100 text-slate-700">{j.status}</Badge></td>
                <td className="px-4 py-3">
                  <Select value={j.driver_id || "none"} onValueChange={(v) => assign(j.id, v === "none" ? null : v)}>
                    <SelectTrigger className="h-8 w-40" data-testid={`job-assign-${j.id}`}><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Unassigned</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {!jobs.length && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">No jobs yet — jobs appear when orders need pickup/delivery.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------- Invoices -------------------------------- */

export default Drivers;
